import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { SSMClient } from '@aws-sdk/client-ssm';
import { Button, Flex, Input, Space, Table, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'common/components/breadcrumbs/breadcrumbs.component.tsx';
import { ENV_AWS_CONFIGS_ROOT, ENV_BACKOFFICE_API_URL } from 'common/constants/env.constants.ts';
import { ELocalStorage } from 'common/enums/local-storage.enums.ts';
import useErrorHandlerHook from 'common/hooks/useErrorHandler.hook.tsx';
import { useAwsDescribeParametersQuery, useAwsInitConfigsEntryReaderQuery } from 'domains/aws/queries/aws.query.ts';
import { useBackofficeDictionaryQuery } from 'domains/backoffice/queries/backoffice.query.ts';
import AddEditGroupConfigurationsDrawer from 'pages/group-configurations/drawer/add-edit-group-configurations.drawer.tsx';
import { EDrawerType } from 'pages/group-terminals/enums/group-terminals.enums.ts';
import {
  IGroupTerminalDrawerData,
  IGroupTerminalTableRowRecord,
} from 'pages/group-terminals/interfaces/group-terminals.interfaces.ts';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants.ts';

const GroupConfigurationsPage = (): ReactElement => {
  const { groupName } = useParams();
  const [groupConfigsDrawerData, setGroupConfigsDrawerData] = useState<
    IGroupTerminalDrawerData | Record<string, never>
  >({});
  const [tableCurrentPage, setTableCurrentPage] = useState<number>(1);
  const [filterMethodGuidValue, setFilterMethodGuidValue] = useState<string>('');
  const token = localStorage.getItem(ELocalStorage.Token) ?? '';
  const { errorHandler } = useErrorHandlerHook();

  const {
    data: awsInitEntryReaderData,
    isLoading: awsInitEntryReaderLoading,
    isError: isAwsInitEntryReaderError,
    error: awsInitEntryReaderError,
  } = useAwsInitConfigsEntryReaderQuery(token, {
    enabled: !!token,
  });

  const ssmClient = awsInitEntryReaderData?.ssmClient as unknown as SSMClient;

  const {
    data: awsDescribeParametersData,
    isLoading: awsDescribeParametersLoading,
    isError: isAwsDescribeParametersError,
    error: awsDescribeParametersError,
  } = useAwsDescribeParametersQuery(
    ssmClient,
    {
      ParameterFilters: [
        {
          Values: [`${ENV_AWS_CONFIGS_ROOT}/entries/${groupName ?? '<!!!unknown-group-name!!!>'}`],
          Key: 'Path',
          Option: 'Recursive',
        },
      ],
    },
    {
      enabled: !!Object.keys(awsInitEntryReaderData ?? {}).length,
    },
  );

  const {
    data: backofficeDictionaryData,
    isLoading: isBackofficeDictionaryLoading,
    isError: isBackofficeDictionaryError,
    error: backofficeDictionaryError,
  } = useBackofficeDictionaryQuery(token, {
    enabled: !!token && !!ENV_BACKOFFICE_API_URL,
  });

  const methodNameByGuid = useMemo(
    () => new Map(backofficeDictionaryData?.paymentMethods.map(({ guid, name }) => [guid, name])),
    [backofficeDictionaryData?.paymentMethods],
  );

  const isTableDataLoading = awsInitEntryReaderLoading || awsDescribeParametersLoading || isBackofficeDictionaryLoading;
  const isTableDataHasError = isAwsInitEntryReaderError || isAwsDescribeParametersError || isBackofficeDictionaryError;
  const tableDataError = awsInitEntryReaderError ?? awsDescribeParametersError ?? backofficeDictionaryError;

  useEffect(() => {
    if (isTableDataHasError && tableDataError) {
      errorHandler(tableDataError);
    }
  }, [errorHandler, isTableDataHasError, tableDataError]);

  const searchByMethodGuidHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFilterMethodGuidValue(event.target.value.toLowerCase());
  };

  const renderNameWithGuid = (name: string | undefined, guid: string | undefined): ReactElement => (
    <Flex vertical>
      {name && <Typography.Text strong>{name}</Typography.Text>}
      <Typography.Text>{guid}</Typography.Text>
    </Flex>
  );

  const tableData = awsDescribeParametersData
    ?.map((group) => {
      const splitName = group.Name?.split('/').slice(-2);
      const methodGUID = splitName?.[0];

      return { methodGUID, key: group.ARN };
    })
    .filter((element) => element.methodGUID?.toLowerCase().includes(filterMethodGuidValue));

  return (
    <div>
      <Breadcrumbs
        groupName={groupName}
        path={ROUTES_MAPPING.PROTECTED.CONFIGURATIONS}
        pageTitle="Configurations Groups"
      />
      <Flex
        justify="space-between"
        gap={24}
        style={{
          paddingBottom: 24,
        }}
      >
        <Input
          placeholder="Search by method GUID"
          size="large"
          style={{
            maxWidth: 400,
          }}
          onChange={searchByMethodGuidHandler}
          suffix={<SearchOutlined />}
          allowClear
        />
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => {
            setGroupConfigsDrawerData({
              type: EDrawerType.AddNew,
              groupName: groupName ?? '',
            });
          }}
        >
          Add new
        </Button>
      </Flex>
      <Table
        dataSource={tableData}
        loading={isTableDataLoading}
        pagination={{
          current: tableCurrentPage,
          pageSize: 30,
        }}
        onChange={(pagination) => {
          setTableCurrentPage(pagination.current ?? 1);
        }}
      >
        <Table.Column
          title="Method GUID"
          dataIndex="methodGUID"
          key="methodGUID"
          width="90%"
          render={(methodGUID: string | undefined) =>
            renderNameWithGuid(methodNameByGuid.get(methodGUID ?? ''), methodGUID)
          }
        />
        <Table.Column
          key="action"
          render={(_, record: IGroupTerminalTableRowRecord) => (
            <Space size="middle">
              <Button
                onClick={() => {
                  setGroupConfigsDrawerData({
                    record,
                    type: EDrawerType.Edit,
                    groupName: groupName ?? '',
                  });
                }}
              >
                Edit
              </Button>
            </Space>
          )}
        />
      </Table>
      <AddEditGroupConfigurationsDrawer
        width="50%"
        open={!!Object.keys(groupConfigsDrawerData).length}
        data={groupConfigsDrawerData}
        methodNameByGuid={methodNameByGuid}
        onClose={() => {
          setGroupConfigsDrawerData({});
        }}
      />
    </div>
  );
};

export default GroupConfigurationsPage;
